import Link from "next/link";
import { formatDate } from "@/lib/utils";
import VoteButtons from "@/components/voting/VoteButtons";
import TagBadge from "@/components/common/TagBadge";

interface Tag {
    id: string;
    name: string;
    slug: string;
    color: string;
}

interface InbCardProps {
    id: string;
    title: string;
    description: string;
    eventDate: Date;
    upvotes: number;
    downvotes: number;
    userVote?: number | null;
    tier?: string;
    authorName?: string;
    tags?: Tag[];
}

export default function InbCard({
    id,
    title,
    description,
    eventDate,
    upvotes,
    downvotes,
    userVote,
    tier,
    authorName,
    tags,
}: InbCardProps) {
    const tierColors: Record<string, string> = {
        S: "from-amber-400 to-yellow-500 text-black",
        A: "from-red-500 to-rose-600 text-white",
        B: "from-blue-500 to-indigo-600 text-white",
        C: "from-green-500 to-emerald-600 text-white",
        D: "from-gray-400 to-gray-500 text-white",
    };

    return (
        <article className="card animate-fadeIn group">
            <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                {/* Vote section */}
                <div className="flex sm:flex-col items-center justify-center">
                    <VoteButtons
                        inbId={id}
                        initialUpvotes={upvotes}
                        initialDownvotes={downvotes}
                        initialUserVote={userVote}
                    />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-3 mb-2">
                        {tier && (
                            <span
                                className={`tier-badge bg-gradient-to-r ${tierColors[tier] || tierColors.D
                                    }`}
                            >
                                {tier}
                            </span>
                        )}
                        <Link href={`/inby/${id}`} className="flex-1">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white group-hover:text-[#6b90fc] transition-colors line-clamp-2">
                                {title}
                            </h3>
                        </Link>
                    </div>

                    <p className="text-gray-600 dark:text-gray-300 text-sm mb-3 line-clamp-2">
                        {description}
                    </p>

                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-300">
                        <span>📅 {formatDate(new Date(eventDate))}</span>
                        {authorName && <span>👤 {authorName}</span>}
                    </div>

                    {tags && tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                            {tags.map((tag) => (
                                <TagBadge
                                    key={tag.id}
                                    name={tag.name}
                                    slug={tag.slug}
                                    color={tag.color}
                                    size="sm"
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </article>
    );
}
